import { useState, useCallback, useMemo, useSyncExternalStore, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { DemoModeContext, getDemoFlag, setDemoFlag } from '../../hooks/useDemoMode'
import { DemoStore } from '../../lib/demo/DemoStore'
import { DEMO_COMPANY_ID } from '../../lib/demo/demoData'
import { ReactQueryDataContext } from '../../lib/ReactQueryDataProvider'
import { useReactQueryDataContext } from '../../lib/ReactQueryDataProvider'
import { useSupabaseAuth } from '../../lib/useSupabaseAuth'
import { supabase } from '../../lib/supabase'

// Persist demo analytics into onboarding_data JSONB on the companies table
async function saveDemoAnalytics(companyId, analytics, exitType) {
  if (!companyId) return
  try {
    const { data: company } = await supabase
      .from('companies')
      .select('onboarding_data')
      .eq('id', companyId)
      .single()

    const currentData = company?.onboarding_data || {}

    await supabase
      .from('companies')
      .update({
        onboarding_data: {
          ...currentData,
          demo_started_at: analytics.started_at,
          demo_exited_at: analytics.exited_at,
          demo_exit_type: exitType, // 'completed' | 'abandoned' | 'heartbeat'
          demo_duration_seconds: analytics.duration_seconds,
          demo_interactions: analytics.interactions,
          demo_pages_visited: analytics.pages_visited,
          demo_features_used: analytics.features_used
        }
      })
      .eq('id', companyId)
  } catch (error) {
    console.warn('[DemoProvider] Failed to save demo analytics:', error)
  }
}

// Wraps children and, when demo mode is active, overrides the ReactQueryDataContext
// with in-memory demo data + CRUD methods that only mutate local state.
const DemoProvider = ({ children }) => {
  const [isDemoMode, setIsDemoMode] = useState(getDemoFlag)
  const storeRef = useRef(null)
  const { companyId } = useSupabaseAuth()

  // Lazily create the store only when entering demo mode
  if (isDemoMode && !storeRef.current) {
    storeRef.current = new DemoStore()
  }

  const enterDemoMode = useCallback(() => {
    storeRef.current = new DemoStore()
    setDemoFlag(true)
    setIsDemoMode(true)
  }, [])

  const exitDemoMode = useCallback(() => {
    // Save analytics before clearing the store
    if (storeRef.current) {
      const analytics = storeRef.current.getAnalytics()
      saveDemoAnalytics(companyId, analytics, 'completed')
    }
    storeRef.current = null
    setDemoFlag(false)
    setIsDemoMode(false)
  }, [companyId])

  // Save partial analytics on page unload (tab close / navigate away)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isDemoMode && storeRef.current) {
        const analytics = storeRef.current.getAnalytics()
        // Use sendBeacon for reliable delivery during page unload
        const payload = JSON.stringify({
          companyId,
          analytics,
          exitType: 'abandoned'
        })
        // Store in sessionStorage as a fallback — the next session init can pick it up
        try {
          sessionStorage.setItem('prosperia_demo_analytics_pending', payload)
        } catch { /* ignore */ }
        // Also fire an async save (best-effort)
        saveDemoAnalytics(companyId, analytics, 'abandoned')
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDemoMode, companyId])

  // On mount, flush any pending analytics from a previous abandoned session
  useEffect(() => {
    try {
      const pending = sessionStorage.getItem('prosperia_demo_analytics_pending')
      if (pending) {
        const { companyId: pendingCompanyId, analytics, exitType } = JSON.parse(pending)
        if (pendingCompanyId) {
          saveDemoAnalytics(pendingCompanyId, analytics, exitType)
        }
        sessionStorage.removeItem('prosperia_demo_analytics_pending')
      }
    } catch { /* ignore */ }
  }, [])

  const demoContextValue = useMemo(() => ({
    isDemoMode,
    demoStore: storeRef.current,
    enterDemoMode,
    exitDemoMode
  }), [isDemoMode, enterDemoMode, exitDemoMode])

  return (
    <DemoModeContext.Provider value={demoContextValue}>
      {isDemoMode ? (
        <DemoDataOverride store={storeRef.current}>
          {children}
        </DemoDataOverride>
      ) : (
        children
      )}
    </DemoModeContext.Provider>
  )
}

// Inner component that overrides ReactQueryDataContext with demo store data.
// Re-renders whenever the store is mutated via useSyncExternalStore.
const DemoDataOverride = ({ store, children }) => {
  const realContext = useReactQueryDataContext()

  // Track page visits as the user navigates in demo mode
  const location = useLocation()
  useEffect(() => {
    if (store) {
      // Normalize path: /estimates/abc123 -> "estimates"
      const segment = location.pathname.split('/').filter(Boolean)[0] || 'dashboard'
      store.trackPageVisit(segment)
    }
  }, [location.pathname, store])

  // Re-render when store data changes
  const storeVersion = useSyncExternalStore(
    store.subscribe.bind(store),
    () => store.data, // getSnapshot
    () => store.data  // getServerSnapshot
  )

  // Helpers to create entity accessors that match the real context shape
  const makeAccessor = useCallback((entity) => ({
    data: store.getAll(entity),
    loading: false,
    error: null,
    isLoaded: true
  }), [storeVersion]) // eslint-disable-line react-hooks/exhaustive-deps

  // Demo-mode CRUD methods that write to the in-memory store
  const demoMethods = useMemo(() => ({
    // ---------- Clients ----------
    addClient: async (clientData) => {
      const result = store.create('clients', clientData)
      return result
    },
    updateClient: async (clientId, clientData) => {
      const result = store.update('clients', clientId, clientData)
      return { data: result }
    },
    deleteClient: async (clientId) => {
      return store.delete('clients', clientId)
    },

    // ---------- Jobs ----------
    addJob: async (jobData) => {
      return store.create('jobs', jobData)
    },
    updateJob: async (jobId, jobData) => {
      const result = store.update('jobs', jobId, jobData)
      return result
    },
    deleteJob: async (jobId) => {
      return store.delete('jobs', jobId)
    },

    // ---------- Estimates ----------
    addEstimate: async (estimateData) => {
      const number = store.generateEstimateNumber()
      return store.create('estimates', { ...estimateData, estimate_number: number })
    },
    updateEstimate: async (estimateId, estimateData) => {
      return store.update('estimates', estimateId, estimateData)
    },
    deleteEstimate: async (estimateId) => {
      return store.delete('estimates', estimateId)
    },

    // ---------- Invoices ----------
    addInvoice: async (invoiceData) => {
      const number = store.generateInvoiceNumber()
      return { data: store.create('invoices', { ...invoiceData, invoice_number: number }) }
    },
    updateInvoice: async (invoiceId, invoiceData) => {
      return { data: store.update('invoices', invoiceId, invoiceData) }
    },
    deleteInvoice: async (invoiceId) => {
      return store.delete('invoices', invoiceId)
    },

    // ---------- Calendar Events ----------
    addCalendarEvent: async (eventData) => {
      return store.create('calendar_events', eventData)
    },
    updateCalendarEvent: async (eventId, eventData) => {
      return store.update('calendar_events', eventId, eventData)
    },
    deleteCalendarEvent: async (eventId) => {
      return store.delete('calendar_events', eventId)
    },

    // ---------- Employees ----------
    addEmployee: async (empData) => {
      return { data: store.create('employees', empData) }
    },
    updateEmployee: async (empId, empData) => {
      return { data: store.update('employees', empId, empData) }
    },
    deleteEmployee: async (empId) => {
      return store.delete('employees', empId)
    },

    // ---------- Automations ----------
    setAutomations: async (automationData) => {
      const result = store.upsert('automations', automationData)
      return { data: result }
    },

    // ---------- Services (via context -- some components use API imports instead) ----------
    // Services are typically created via direct API imports, handled in Phase 4.
    // But if any component uses context methods, they'll hit these.

    // ---------- Other legacy / no-op methods ----------
    deleteTemplate: async () => ({ data: null }),
    updateConversation: async () => ({ data: null }),
    deleteConversation: async () => ({ data: null }),
    upsertAvailability: async (data) => data,

    // ---------- Data refresh (no-op in demo) ----------
    refreshData: async () => {},
    warmCache: () => {}
  }), [store, storeVersion]) // eslint-disable-line react-hooks/exhaustive-deps

  // Build the overridden context value
  const value = useMemo(() => {
    // All entity accessors
    const entities = {}
    const entityNames = Object.keys(store.data)
    for (const name of entityNames) {
      entities[name] = makeAccessor(name)
    }
    // Backward compat alias
    entities.calendarEvents = entities.calendar_events

    return {
      // Spread all entity accessors
      ...entities,

      // Core fields
      data: store.data,
      loading: false,
      error: null,
      companyId: DEMO_COMPANY_ID,
      user: realContext.user, // keep the real user for display purposes
      isDataReady: true,
      isInitializing: false,
      loadingState: { isInitializing: false, isBackgroundRefreshing: false, errors: [] },
      performanceMetrics: {},

      // Data access helpers
      getData: (entityName) => ({
        data: store.getAll(entityName),
        isLoading: false,
        isLoaded: true,
        error: null
      }),

      // CRUD methods
      ...demoMethods,

      // Legacy compat
      initialized: true,
      communications: [],
      consentLogs: [],
      templates: [],
      smsSettings: { from: '+15551234567', quietHours: { start: '22:00', end: '08:00' } },
      serviceCenter: {},
      integrations: [],
      reports: [],
      scheduling: [],
      inspection: [],
      selfService: {},
      publicEstimate: {},
      paymentDashboard: {},
      itemLibrary: {},
      automationsSettings: {},
      availability: []
    }
  }, [store, storeVersion, makeAccessor, demoMethods, realContext.user]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ReactQueryDataContext.Provider value={value}>
      {children}
    </ReactQueryDataContext.Provider>
  )
}

export default DemoProvider
